/* Nekkat - estimation de livraison a deux paliers
 * Paliers pilotes par le tag produit : stock-eu / stock-cn
 * Jours ouvres : lundi au samedi, hors feries francais
 * Repli : palier long si aucun tag reconnu
 *
 * L'etape "Commande" affiche toujours "Aujourd'hui" : le client doit pouvoir
 * commander a n'importe quelle heure sans qu'on lui suggere d'attendre.
 * L'heure limite ne sert plus qu'a calculer la date d'expedition.
 */

const NK_HOLIDAY_CACHE = {};

function nkEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function nkHolidayKey(date) {
  return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
}

function nkHolidaysForYear(year) {
  if (NK_HOLIDAY_CACHE[year]) return NK_HOLIDAY_CACHE[year];

  const set = new Set();
  const easter = nkEasterSunday(year);
  const fromEaster = (offset) => {
    const d = new Date(easter.getTime());
    d.setDate(d.getDate() + offset);
    return d;
  };

  [
    new Date(year, 0, 1),    // Jour de l'an
    fromEaster(1),           // Lundi de Paques
    new Date(year, 4, 1),    // Fete du travail
    new Date(year, 4, 8),    // Victoire 1945
    fromEaster(39),          // Ascension
    fromEaster(50),          // Lundi de Pentecote
    new Date(year, 6, 14),   // Fete nationale
    new Date(year, 7, 15),   // Assomption
    new Date(year, 10, 1),   // Toussaint
    new Date(year, 10, 11),  // Armistice 1918
    new Date(year, 11, 25)   // Noel
  ].forEach((d) => set.add(nkHolidayKey(d)));

  NK_HOLIDAY_CACHE[year] = set;
  return set;
}

function nkIsWorkingDay(date, sundayOff, saturdayOff) {
  const day = date.getDay();
  if (sundayOff && day === 0) return false;
  if (saturdayOff && day === 6) return false;
  return !nkHolidaysForYear(date.getFullYear()).has(nkHolidayKey(date));
}

class NkDeliveryEstimation extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  readConfig() {
    const ds = this.dataset;
    const tier = (ds.tier || 'cn').toLowerCase();
    const isEu = tier === 'eu';

    return {
      locale: ds.locale || 'fr',
      minDays: parseInt(isEu ? ds.euMin : ds.cnMin, 10) || (isEu ? 3 : 6),
      maxDays: parseInt(isEu ? ds.euMax : ds.cnMax, 10) || (isEu ? 5 : 8),
      cutoffHour: parseInt(ds.cutoffHour, 10),
      saturdayOff: ds.saturdayOff === 'true',
      sundayOff: ds.sundayOff !== 'false'
    };
  }

  shippingDate(cfg, now) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const missedCutoff = !isNaN(cfg.cutoffHour) && now.getHours() >= cfg.cutoffHour;

    if (missedCutoff) d.setDate(d.getDate() + 1);
    while (!nkIsWorkingDay(d, cfg.sundayOff, cfg.saturdayOff)) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  addWorkingDays(start, count, cfg) {
    const d = new Date(start.getTime());
    let added = 0;
    let guard = 0;

    while (added < count && guard < 400) {
      d.setDate(d.getDate() + 1);
      guard++;
      if (nkIsWorkingDay(d, cfg.sundayOff, cfg.saturdayOff)) added++;
    }
    return d;
  }

  format(date, locale, opts) {
    const s = new Intl.DateTimeFormat(locale, opts).format(date);
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  render() {
    const cfg = this.readConfig();
    const now = new Date();

    const ship = this.shippingDate(cfg, now);
    const min = this.addWorkingDays(ship, cfg.minDays, cfg);
    const max = this.addWorkingDays(ship, cfg.maxDays, cfg);

    const sameMonth = min.getMonth() === max.getMonth();
    const dayOnly = { day: 'numeric' };
    const dayMonth = { day: 'numeric', month: 'long' };
    const weekdayDayMonth = { weekday: 'long', day: 'numeric', month: 'long' };

    const orderEl = this.querySelector('[data-ref="order-date"]');
    const shipEl = this.querySelector('[data-ref="ship-date"]');
    const rangeEl = this.querySelector('[data-ref="delivery-range"]');
    const noteEl = this.querySelector('[data-ref="delay-note"]');

    /* Toujours "Aujourd'hui", quelle que soit l'heure. */
    if (orderEl) {
      orderEl.textContent = 'Aujourd\u2019hui';
    }

    if (shipEl) {
      shipEl.textContent = this.format(ship, cfg.locale, weekdayDayMonth);
    }

    if (rangeEl) {
      rangeEl.textContent = sameMonth
        ? this.format(min, cfg.locale, dayOnly) + ' \u2013 ' + this.format(max, cfg.locale, dayMonth)
        : this.format(min, cfg.locale, dayMonth) + ' \u2013 ' + this.format(max, cfg.locale, dayMonth);
    }

    if (noteEl) {
      noteEl.textContent = 'Livraison estim\u00e9e sous ' + cfg.minDays + ' \u00e0 ' + cfg.maxDays + ' jours ouvr\u00e9s';
    }

    this.removeAttribute('hidden');
  }
}

if (!customElements.get('nk-delivery-estimation')) {
  customElements.define('nk-delivery-estimation', NkDeliveryEstimation);
}
